import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui";

export type SelectableListOption = {
  _id: string;
  listName?: string;
  prospectsCount?: number;
};

type SelectListForImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lists: SelectableListOption[];
  onSelect: (listId: string) => void;
  title?: string;
  continueLabel?: string;
  isSubmitting?: boolean;
};

export default function SelectListForImportModal({
  isOpen,
  onClose,
  lists,
  onSelect,
  title = "Select a list to import into",
  continueLabel = "Continue to Import",
  isSubmitting = false,
}: SelectListForImportModalProps) {
  const [selectedListId, setSelectedListId] = useState<string>("");
  const [search, setSearch] = useState("");
  const wasOpenRef = useRef(false);

  // Only reset selection when the modal opens — not on every lists array identity change.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSearch("");
      const firstId = lists[0]?._id ? String(lists[0]._id) : "";
      setSelectedListId(firstId);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, lists]);

  const filteredLists = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lists;
    return lists.filter((list) =>
      String(list.listName || "").toLowerCase().includes(q)
    );
  }, [lists, search]);

  const effectiveSelectedId =
    selectedListId ||
    (filteredLists[0]?._id ? String(filteredLists[0]._id) : "");

  const handleContinue = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    const id = String(effectiveSelectedId || "").trim();
    if (!id) return;
    onSelect(id);
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="modal-overlay fixed inset-0 z-[10050] flex items-center justify-center bg-black/[0.31] p-4"
      onMouseDown={(event) => {
        // Click on backdrop closes; clicks inside panel must not bubble to page handlers.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal-container max-h-[calc(100dvh-2rem)] w-full max-w-[620px] overflow-y-auto rounded-[16px] bg-white p-4 shadow-sm"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative rounded-t-[12px] border border-b-0 border-border-grey bg-white px-4 pt-3">
          <h2 className="m-0 mt-8 text-left text-md font-medium leading-none text-dark-black">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-0 top-0 flex h-10 w-10 items-center justify-center rounded-full hover:bg-primary"
          >
            <img src="/icons/x_circle.svg" alt="" aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>

        <div className="rounded-b-[12px] border border-t-0 border-border-grey bg-white px-4 py-4">
          <div className="space-y-4">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search lists"
              className="h-10 w-full rounded-[4px] border border-light-grey bg-white px-3 text-sm text-dark-black outline-none placeholder:text-dark-grey focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
            />

            <div className="noScroll flex max-h-[40vh] flex-col gap-2 overflow-y-auto">
              {filteredLists.length === 0 ? (
                <p className="px-1 py-6 text-center text-sm text-dark-grey">No lists found</p>
              ) : (
                filteredLists.map((list) => {
                  const id = String(list._id || "");
                  const selected = effectiveSelectedId === id;
                  const count = Number(list.prospectsCount || 0);
                  return (
                    <button
                      key={id}
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setSelectedListId(id);
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setSelectedListId(id);
                      }}
                      className={`${
                        selected
                          ? "border-brand-blue bg-primary"
                          : "border-light-grey bg-background-grey hover:border-brand-blue/40"
                      } flex w-full items-center justify-between gap-3 rounded-[8px] border px-4 py-3 text-left transition-colors`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-dark-black">
                          {list.listName || "Untitled list"}
                        </span>
                        <span className="mt-0.5 block text-xs text-dark-grey">
                          {count} {count === 1 ? "prospect" : "prospects"}
                        </span>
                      </span>
                      <span
                        className={`${
                          selected
                            ? "border-brand-blue bg-brand-blue"
                            : "border-light-grey bg-white"
                        } flex h-4 w-4 shrink-0 items-center justify-center rounded-full border`}
                        aria-hidden="true"
                      >
                        {selected ? (
                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                        ) : null}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <Button
              variant="primary"
              size="sm"
              fullWidth
              isDisabled={!effectiveSelectedId || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleContinue}
            >
              {continueLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
